/**
 * Client-side predicates for the add-players drawer's filtered view (SE-7090,
 * SE-7094).
 *
 * The drawer's committed advanced filters are evaluated HERE, over the union
 * of the drawer's own pools (Watchlists + My Roster + Suggested), instead of
 * being sent to the global `filterPlayers` search — that global read is the
 * SE-7090 bug: it bypassed the drawer's pools entirely and produced false
 * 0-result filters for players the drawer itself offers.
 *
 * ## Server parity
 *
 * The input evaluated is the SAME `PlayerAdvancedFilterInput` object
 * `buildAdvancedFilterInput` produces for the server path, so filter-activity
 * semantics (untouched range → key omitted → no constraint) are inherited
 * rather than re-derived. Each predicate mirrors the backend comparison
 * (`pivoted-stats-view.repository.ts`):
 *
 * - `roleArchetypes` matches PRIMARY OR SECONDARY archetype.
 * - `minShooting`/`maxShooting` compare the SHOT_LOCATION score — the
 *   backend's own mapping for the Shooting filter.
 * - `minFitScore`/`maxFitScore` arrive already scaled to the DB's 0–1
 *   decimal (`scaleFitScore`), matching `bioData.fitScore`.
 * - `nationalities` are NAME strings (`useFilterVariables` resolves the
 *   selected ids to labels before building the input), compared against
 *   `bioData.nationality`; `minMonthsRemaining`/`maxMonthsRemaining`
 *   compare months derived from `contractExpires`, the same
 *   `differenceInMonths` derivation `playerFormatting` uses.
 * - `customStats` (REPORT STATS, SE-8608) compare
 *   `bioData.customStats[statName].value` as a number — the backend's
 *   `(CUSTOM_STATS->statName->>'value')::NUMERIC`.
 * - A player with NO value for a bounded metric is EXCLUDED while that bound
 *   is active — SQL comparison semantics, and what the server path did.
 * @module features/player-kanban/utils/addPlayersFilterPredicate
 */

import { differenceInMonths } from "date-fns";

import type { PlayerAdvancedFilterInput } from "@/generated/graphql";

/**
 * The filterable shape a union-pool row must expose — structural, so the
 * `KanbanFilterablePlayerData` pool rows and the enriched suggested-candidate
 * players both satisfy it.
 */
export interface FilterablePoolPlayer {
  readonly id: string;
  readonly positions?: readonly ({ readonly id: string } | null)[] | null;
  readonly team?: { readonly id: string } | null;
  readonly league?: { readonly id: string } | null;
  readonly watchlists?: readonly ({ readonly id: string } | null)[] | null;
  readonly bioData?: {
    readonly age?: number | null;
    readonly gpr?: number | null;
    readonly nationality?: string | null;
    readonly contractExpires?: string | null;
    readonly roleArchetype?: string | null;
    readonly secondaryRoleArchetype?: string | null;
    readonly geminiPlayerValuation?: number | null;
    readonly fitScore?: number | null;
    readonly teamStyleFit?: number | null;
    readonly carryingTransGlobalCategoricalScore?: number | null;
    readonly dribblingTransGlobalCategoricalScore?: number | null;
    readonly passingTransGlobalCategoricalScore?: number | null;
    readonly shotLocationTransGlobalCategoricalScore?: number | null;
    readonly gkPassingTransCategoricalScore?: number | null;
    readonly gkShotStoppingTransCategoricalScore?: number | null;
    readonly gkPositioningTransCategoricalScore?: number | null;
    /**
     * The org's custom stats JSON — `{ [statName]: { value } }`, value a
     * numeric string (`BackendCustomStats`). Typed loosely because the
     * generated scalar is `JSONObject`.
     */
    readonly customStats?: unknown;
  } | null;
}

/**
 * Months remaining on a player's contract, or null when unknown — the same
 * floor-at-zero derivation `formatContractMonths` uses.
 * @param contractExpires - ISO expiry date, if any.
 * @returns Whole months remaining, or null.
 */
const monthsRemaining = (
  contractExpires: string | null | undefined
): number | null =>
  contractExpires
    ? Math.max(0, differenceInMonths(new Date(contractExpires), new Date()))
    : null;

/**
 * Whether a value satisfies an inclusive min/max pair. An absent pair is no
 * constraint; an active bound EXCLUDES players without a value (server
 * parity — SQL `NULL >= x` is not true).
 * @param value - The player's metric value.
 * @param min - Inclusive lower bound, when active.
 * @param max - Inclusive upper bound, when active.
 * @returns Whether the value passes.
 */
const withinBounds = (
  value: number | null | undefined,
  min: number | null | undefined,
  max: number | null | undefined
): boolean => {
  if (min == null && max == null) return true;
  if (value == null) return false;
  if (min != null && value < min) return false;
  if (max != null && value > max) return false;
  return true;
};

/**
 * One custom stat's numeric value, or null when the player has none — the
 * client twin of the backend's `(CUSTOM_STATS->name->>'value')::NUMERIC`.
 * @param customStats - The player's `bioData.customStats` JSON.
 * @param statName - The stat key the filter names.
 * @returns The value as a number, or null.
 */
const customStatValue = (
  customStats: unknown,
  statName: string
): number | null => {
  if (customStats == null || typeof customStats !== "object") return null;
  const entry = (customStats as Record<string, unknown>)[statName];
  if (entry == null || typeof entry !== "object") return null;
  const raw = (entry as { readonly value?: unknown }).value;
  if (raw == null || raw === "") return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

/**
 * Whether an id-list filter passes: no selection is no constraint, otherwise
 * the player's id must be among the selected.
 * @param playerValue - The player's id for this dimension, if any.
 * @param selectedIds - The committed selection.
 * @returns Whether the dimension passes.
 */
const matchesIdSelection = (
  playerValue: string | null | undefined,
  selectedIds: readonly string[] | null | undefined
): boolean => {
  if (selectedIds == null || selectedIds.length === 0) return true;
  return playerValue != null && selectedIds.includes(playerValue);
};

/**
 * Every metric bound the drawer's panel can commit, paired with the player
 * field it compares — one place to keep the backend column parity readable.
 * @param player - The union-pool row.
 * @param input - The committed advanced filter input.
 * @returns The [value, min, max] triples to evaluate.
 */
const metricBounds = (
  player: FilterablePoolPlayer,
  input: PlayerAdvancedFilterInput
): readonly (readonly [
  number | null | undefined,
  number | null | undefined,
  number | null | undefined,
])[] => {
  const bio = player.bioData;
  return [
    [bio?.age, input.minAge, input.maxAge],
    [
      monthsRemaining(bio?.contractExpires),
      input.minMonthsRemaining,
      input.maxMonthsRemaining,
    ],
    [
      bio?.geminiPlayerValuation,
      input.minGeminiPlayerValuation,
      input.maxGeminiPlayerValuation,
    ],
    [bio?.gpr, input.minGpr, input.maxGpr],
    [bio?.fitScore, input.minFitScore, input.maxFitScore],
    [bio?.teamStyleFit, input.minTeamStyleFit, input.maxTeamStyleFit],
    [
      bio?.carryingTransGlobalCategoricalScore,
      input.minCarrying,
      input.maxCarrying,
    ],
    [
      bio?.dribblingTransGlobalCategoricalScore,
      input.minDribbling,
      input.maxDribbling,
    ],
    [
      bio?.passingTransGlobalCategoricalScore,
      input.minPassing,
      input.maxPassing,
    ],
    [
      bio?.shotLocationTransGlobalCategoricalScore,
      input.minShooting,
      input.maxShooting,
    ],
    [
      bio?.gkPassingTransCategoricalScore,
      input.minGkPassing,
      input.maxGkPassing,
    ],
    [
      bio?.gkShotStoppingTransCategoricalScore,
      input.minGkShotStopping,
      input.maxGkShotStopping,
    ],
    [
      bio?.gkPositioningTransCategoricalScore,
      input.minGkPositioning,
      input.maxGkPositioning,
    ],
  ];
};

/**
 * Whether a union-pool player passes the committed advanced filters.
 * @param player - The union-pool row.
 * @param input - The committed `PlayerAdvancedFilterInput`, as built by
 * `buildAdvancedFilterInput` (untouched groups are absent keys).
 * @returns Whether the player belongs in the filtered results.
 */
export const matchesAdvancedFilters = (
  player: FilterablePoolPlayer,
  input: PlayerAdvancedFilterInput
): boolean => {
  const positionIds = input.positionIds ?? [];
  if (positionIds.length > 0) {
    const playerPositionIds = (player.positions ?? []).flatMap(position =>
      position ? [position.id] : []
    );
    if (!playerPositionIds.some(id => positionIds.includes(id))) return false;
  }

  if (!matchesIdSelection(player.team?.id, input.teamIds)) return false;
  if (!matchesIdSelection(player.league?.id, input.leagueIds)) return false;

  const nationalities = input.nationalities ?? [];
  if (nationalities.length > 0) {
    const nationality = player.bioData?.nationality;
    if (nationality == null || !nationalities.includes(nationality)) {
      return false;
    }
  }

  const roles = input.roleArchetypes ?? [];
  if (roles.length > 0) {
    const primary = player.bioData?.roleArchetype;
    const secondary = player.bioData?.secondaryRoleArchetype;
    const matchesRole =
      (primary != null && roles.includes(primary)) ||
      (secondary != null && roles.includes(secondary));
    if (!matchesRole) return false;
  }

  const customStatsPass = (input.customStats ?? []).every(filter =>
    withinBounds(
      customStatValue(player.bioData?.customStats, filter.statName),
      filter.minValue,
      filter.maxValue
    )
  );
  if (!customStatsPass) return false;

  return metricBounds(player, input).every(([value, min, max]) =>
    withinBounds(value, min, max)
  );
};

/**
 * Whether a player belongs to any of the selected lists (SE-7094). No
 * selection is no constraint; with a selection, membership in ANY selected
 * watchlist passes — the Squad Planning Lists-filter semantics.
 * @param player - The union-pool row.
 * @param selectedListIds - The committed LISTS selection.
 * @returns Whether the player passes the lists dimension.
 */
export const matchesListSelection = (
  player: FilterablePoolPlayer,
  selectedListIds: readonly string[]
): boolean => {
  if (selectedListIds.length === 0) return true;
  return (player.watchlists ?? []).some(
    watchlist => watchlist != null && selectedListIds.includes(watchlist.id)
  );
};
